import { useState } from "react";
import { UserCheck, X } from "lucide-react";
import { PageHeader } from "@/components/control/PageHeader";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Section, Loadable, Empty, Status, Tabbar, TabPanel, useTabs, RowLink, SelectField, TextField, useLoad, fmt, deText,
} from "@/components/control/kit";

function FilterChips({ chips, onReset }) {
  if (!chips.length) return null;
  return (
    <div className="-mt-2 mb-5 flex flex-wrap items-center gap-2" data-testid="reviews-active-filters">
      <span className="text-sm text-zinc-700">Aktive Filter:</span>
      {chips.map((c) => (
        <button key={c.key} type="button" onClick={c.clear} aria-label={`Filter ${c.label} entfernen`} data-testid={`reviews-chip-${c.key}`}
          className="inline-flex min-h-[32px] items-center gap-1 rounded-full border border-brand-purple/30 bg-brand-purple/5 px-3 text-sm text-brand-purple hover:bg-brand-purple/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-magenta">
          {c.label}: {c.value} <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      ))}
      <button type="button" onClick={onReset} data-testid="reviews-filters-reset" className="text-sm font-semibold text-brand-magenta-dark underline-offset-2 hover:underline">Alle Filter zurücksetzen</button>
    </div>
  );
}

const ASSIGNED_LABEL = { me: "Mir zugewiesen", none: "Nicht zugewiesen" };

export default function Reviews() {
  const [status, setStatus] = useState("offen");
  const [org, setOrg] = useState("all");
  const [assigned, setAssigned] = useState("all");
  const [from, setFrom] = useState("");
  const [q, setQ] = useState("");
  const st = useLoad(`/reviews?${new URLSearchParams({ status, organization: org, assigned, submitted_from: from, q })}`);
  const counts = st.data?.counts || {};
  const tabs = useTabs([
    { value: "offen", label: "Offen", count: counts.offen }, { value: "rückfrage", label: "Rückfrage", count: counts["rückfrage"] },
    { value: "genehmigt", label: "Genehmigt", count: counts.genehmigt }, { value: "abgelehnt", label: "Abgelehnt", count: counts.abgelehnt },
    { value: "all", label: "Alle" },
  ], status);
  const chips = [
    q && { key: "name", label: "Name", value: q, clear: () => setQ("") },
    org !== "all" && { key: "org", label: "Organisation", value: org, clear: () => setOrg("all") },
    assigned !== "all" && { key: "assigned", label: "Zuständigkeit", value: ASSIGNED_LABEL[assigned], clear: () => setAssigned("all") },
    from && { key: "from", label: "Eingereicht ab", value: from, clear: () => setFrom("") },
  ].filter(Boolean);
  const reset = () => { setQ(""); setOrg("all"); setAssigned("all"); setFrom(""); };

  return (
    <div>
      <PageHeader title="Profilprüfungen" subtitle="Eingereichte öffentliche Profile prüfen, nachfragen, freigeben oder ablehnen." icon={UserCheck} />
      <Tabbar tabs={tabs} onChange={setStatus} testId="reviews-status-tabs" />
      <div className="mb-5 grid gap-3 rounded-xl border border-border bg-white p-4 sm:grid-cols-2 lg:grid-cols-4">
        <TextField id="rev-q" label="Name" value={q} onChange={setQ} testId="reviews-search" />
        <SelectField id="rev-org" label="Organisation" value={org} onChange={setOrg} testId="reviews-org-filter"
          options={[{ value: "all", label: "Alle" }, ...(st.data?.organizations || []).map((o) => ({ value: o, label: o }))]} />
        <SelectField id="rev-assigned" label="Zuständigkeit" value={assigned} onChange={setAssigned} testId="reviews-assigned-filter"
          options={[{ value: "all", label: "Alle" }, { value: "me", label: "Mir zugewiesen" }, { value: "none", label: "Nicht zugewiesen" }]} />
        <TextField id="rev-from" label="Eingereicht ab" type="date" value={from} onChange={setFrom} testId="reviews-from-filter" />
      </div>
      <FilterChips chips={chips} onReset={reset} />
      <TabPanel tabs={tabs} testId="reviews-status-tabs-panel">
      <Section title="Prüfungen" testId="reviews-section">
        <Loadable state={st}>
          {(d) => (
            <div className="overflow-x-auto">
              <Table data-testid="reviews-table">
                <TableHeader><TableRow>
                  <TableHead>Mitglied</TableHead><TableHead>Art</TableHead><TableHead>Eingereicht</TableHead>
                  <TableHead>Zuständig</TableHead><TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {d.reviews.length === 0 && <TableRow><TableCell colSpan={5}><Empty /></TableCell></TableRow>}
                  {d.reviews.map((r) => (
                    <TableRow key={r.id} data-testid={`review-row-${r.id}`}>
                      <TableCell>
                        <RowLink to={`/profilpruefungen/${r.id}`} testId={`review-link-${r.id}`}>{r.member_name}</RowLink>
                        <p className="text-xs text-muted-foreground">{r.member_city} · {r.organization}</p>
                      </TableCell>
                      <TableCell>{deText(r.type)}{r.changed_fields?.length ? <span className="ml-2 rounded bg-orange-100 px-1.5 text-xs text-orange-800">überarbeitet</span> : null}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">{fmt(r.submitted_at)}</TableCell>
                      <TableCell className="text-sm">{r.assigned_to?.name || "–"}</TableCell>
                      <TableCell><Status value={r.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Loadable>
      </Section>
      </TabPanel>
    </div>
  );
}
